/**
 * JotRx Setup Banner
 *
 * Dismissible promotional banner on the encounter page advertising JotRx e-prescribing.
 * Shown to users who do NOT yet have the JotRx entitlement. Users who already have
 * JotRx see the setup Callout/Card components on settings pages instead.
 *
 * When the JotRx bundle is not yet available for purchase, only the "Book a setup call"
 * button is shown. The setup CTA and subtitle appear once the bundle is purchasable.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import MedicationIcon from '@mui/icons-material/Medication';
import { alpha, Box, Button, IconButton, Typography } from '@mui/material';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { useStatsigClient } from '@statsig/react-bindings';
import { useSnackbar } from 'notistack';

import { useJotRxSetupState } from '../hooks/useJotRxSetupState';
import { useLazyGetClinicianSsoQuery } from '../store';

const STORAGE_KEY = 'jotrx_setup_banner_dismissed';
const FEATURE_GATE = 'jotrx_ad';
const DYNAMIC_CONFIG = 'jotrx_setup_config';

function getSubtitle(setupState: string): string {
  switch (setupState) {
    case 'idp_complete':
      return 'Identity verified! Open DoseSpot to set up two-factor authentication (TFA) and start prescribing.';
    case 'registered':
      return 'Almost there! Complete identity proofing in DoseSpot to start writing prescriptions.';
    case 'practice_connected':
      return 'Complete your clinician setup to start writing prescriptions.';
    default:
      return 'Connect your practice to DoseSpot to start e-prescribing from the same place you take notes.';
  }
}

export function JotRxSetupBanner() {
  const navigate = useNavigate();
  const { client } = useStatsigClient();
  const isGateEnabled = useFeatureGate(FEATURE_GATE);
  const { setupState, isLoading, isFullySetup, isJotRxPurchasable, cta } = useJotRxSetupState();
  const { jotrx } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const [getClinicianSso] = useLazyGetClinicianSsoQuery();

  const shouldOpenDoseSpot = setupState === 'registered' || setupState === 'idp_complete';

  const setupCallUrl = useMemo(() => {
    const config = client.getDynamicConfig(DYNAMIC_CONFIG);
    return (config.value as { setup_call_url?: string }).setup_call_url || '';
  }, [client]);

  const [dismissedForState, setDismissedForState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || '',
  );

  const isDismissed = dismissedForState === setupState;
  const isVisible = isGateEnabled && !isFullySetup && !isDismissed && !isLoading;

  useEffect(() => {
    if (isVisible) {
      jotrx.bannerShown({ setup_state: setupState });
    }
  }, [isVisible, jotrx, setupState]);

  const dismiss = useCallback(() => {
    setDismissedForState(setupState);
    localStorage.setItem(STORAGE_KEY, setupState);
  }, [setupState]);

  const handleDismiss = useCallback(() => {
    dismiss();
    jotrx.bannerDismissed({ setup_state: setupState });
  }, [dismiss, jotrx, setupState]);

  const handleCtaClick = useCallback(async () => {
    jotrx.setupStarted({ source: 'recorder_banner', setup_state: setupState });

    if (shouldOpenDoseSpot) {
      const ssoWindow = window.open('about:blank', '_blank');
      if (!ssoWindow) {
        enqueueSnackbar('Popup blocked — please allow popups for this site.', { variant: 'warning' });
        return;
      }
      try {
        const result = await getClinicianSso().unwrap();
        if (result.sso_url) {
          ssoWindow.opener = null;
          ssoWindow.location.href = result.sso_url;
          return;
        }
        ssoWindow.close();
      } catch {
        ssoWindow.close();
        // Fall through to navigate if SSO fails
      }
    }

    navigate(cta.route);
  }, [jotrx, setupState, shouldOpenDoseSpot, getClinicianSso, enqueueSnackbar, cta.route, navigate]);

  const handleBookCall = useCallback(() => {
    if (!setupCallUrl) return;
    jotrx.setupCallBooked({ source: 'recorder_banner' });
    window.open(setupCallUrl, '_blank', 'noopener,noreferrer');
  }, [jotrx, setupCallUrl]);

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderBottomColor: theme => alpha(theme.palette.primary.main, 0.15),
        bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
          flexShrink: 0,
        }}
      >
        <MedicationIcon sx={{ fontSize: 18, color: 'primary.main' }} />
      </Box>

      {/* Copy */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          You can now prescribe directly from JotPsych
        </Typography>
        {isJotRxPurchasable && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {getSubtitle(setupState)}
          </Typography>
        )}
      </Box>

      {/* CTAs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pr: 3 }}>
        {isJotRxPurchasable && (
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleCtaClick}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: 1.5,
              px: 2,
              py: 0.75,
              boxShadow: 1,
            }}
          >
            {cta.label}
          </Button>
        )}
        {setupCallUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleBookCall}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: 1.5,
              px: 2,
              py: 0.75,
              borderColor: 'divider',
              color: 'text.primary',
              bgcolor: 'background.paper',
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'divider',
              },
            }}
          >
            Book a setup call
          </Button>
        )}
      </Box>

      {/* Dismiss */}
      <IconButton
        size="small"
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          p: 0.5,
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}
